import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { JobStatusPipe, LoaderComponent } from '@sites/dashboard/ui';
import { Job, Job_Progress, Job_Status } from '@sites/data-hmm/hmm-job';
import { jobComponentActions } from '../store/feature/feature.actions';

/** Text-colour class per job status, driving the square glyph + status word. */
const TONE: Record<Job_Status, string> = {
  [Job_Status.UNKNOWN]: 'tw-text-night-3',
  [Job_Status.RUNNING]: 'tw-text-leaf',
  [Job_Status.SUCCESS]: 'tw-text-leaf',
  [Job_Status.FAILURE]: 'tw-text-raspberry',
  [Job_Status.CANCELLED]: 'tw-text-slate',
};

/** View model for one child step's square glyph and percentage. */
interface ChildStep {
  message: string;
  label: string;
  /** Raw progress handed to the glyph: negative traces, 0–100 fills. */
  value: number;
}

/**
 * Normalise a raw progress `value` to a 0–1 fraction, or `-1` when
 * indeterminate. Accepts both fractions (0–1) and percentages (0–100), matching
 * the convention the backend and the ring progress component already use.
 */
function normalise(value: number): number {
  if (value < 0) return -1;
  if (value > 1) return Math.min(value / 100, 1);
  return Math.min(Math.max(value, 0), 1);
}

@Component({
  selector: 'dashboard-feature-job-job',
  imports: [LoaderComponent, JobStatusPipe],
  templateUrl: './job.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobComponent {
  readonly job = input<Job>();

  private store = inject(Store);

  statuses = Job_Status;

  /** Overall progress as a 0–1 fraction, or `-1` while indeterminate. */
  readonly progress = computed(() =>
    normalise(this.job()?.workflowProgress?.value ?? -1)
  );

  readonly isIndeterminate = computed(() => this.progress() < 0);

  /** Headline percentage, e.g. `42%`, or an em dash while indeterminate. */
  readonly overallLabel = computed(() =>
    this.isIndeterminate() ? '—' : `${Math.round(this.progress() * 100)}%`
  );

  /** Backend progress message, with a fallback while a running job is quiet. */
  readonly progressMessage = computed(() => {
    const message = this.job()?.workflowProgress?.message?.trim();
    if (message) return message;
    return this.job()?.status === Job_Status.RUNNING
      ? 'Waiting for progress…'
      : '';
  });

  /** Raw value handed to the square glyph: live progress, or full when done. */
  readonly glyphValue = computed(() =>
    this.job()?.status === Job_Status.RUNNING
      ? (this.job()?.workflowProgress?.value ?? -1)
      : 100
  );

  readonly toneClass = computed(
    () => TONE[this.job()?.status ?? Job_Status.UNKNOWN]
  );

  /** Started child steps, each as a segmented bar. Not-yet-started are hidden. */
  readonly children = computed<ChildStep[]>(() =>
    (this.job()?.childProgress ?? [])
      .filter((child) => child.value !== 0)
      .map((child) => this.toChildStep(child))
  );

  private toChildStep(child: Job_Progress): ChildStep {
    const fraction = normalise(child.value);
    // A `-1` heartbeat means "started, percentage unknown" — activities emit one
    // before their first real measurement. It stays negative so the glyph traces
    // rather than showing a dead 0% fill, which would read as a stalled step.
    return {
      message: child.message,
      label: fraction < 0 ? '—' : `${Math.round(fraction * 100)}%`,
      value: child.value,
    };
  }

  cancelJob(job: Job) {
    this.store.dispatch(
      jobComponentActions.cancelJob({
        job,
      })
    );
  }
}
